import React, { Component } from "react";
import { BuildingPickerItemFormatter, FormProps, FormState, IPForm, LandPickerItemFormatter, sortRVXLookups } from "./index";
import { PropertiesResponse, RVEnvironment, RVPicketItem, RVPropertyType, RVSearchType, SearchOption } from "../../../api/entities";
import { NullHandler } from "@my-dld/utils/ApiUtils";
import * as RVApi from "../../../api/api";
import { DefaultSearchOption } from "../../../api/api";
import { hideLoading, showLoading, showMessage } from "@my-dld/utils/UIUtils";
import { Property } from "../../../api/Property";
import { LocaleUtil } from "@eres/core";
import PickerInput from "@my-dld/components/Form/PickerInput";
import Strings from "@my-dld/assets/strings";
import { populateSearchOptions } from "../../../RVUtils";
import { checkNonEmpty } from "@eres/ejari-web/utils";
import { ErrorContainer } from "@eres/forms";

interface State extends FormState {
	isNoProperty: boolean;
	number?: string;
	subNo?: string;
	areas?: RVApi.RVLookupX[];
	lands?: Property[];
	buildings?: Property[];
	villas?: Property[];
	units?: Property[];

	hasLand?: boolean;
	hasBuilding?: boolean;

	area?: RVApi.RVLookupX;
	land?: Property;
	building?: Property;
}

export class OwnedForm extends Component<FormProps, State> implements IPForm {
	apiHandler: any;
	apiExHandler: any;

	constructor( props: FormProps ) {
		super( props );
		this.state = {
			isNoProperty: false,
			errorContainer: {},
			hasLand: this.props.env.propertyType !== RVPropertyType.LAND,
			hasBuilding: this.props.env.propertyType === RVPropertyType.UNIT,
		};
	}

	static getDerivedStateFromProps( nextProps: Readonly<FormProps>, prevState: State ): Partial<State> {
		let propertyType = nextProps.env.propertyType;
		const update: Partial<State> = {
			hasLand: propertyType !== RVPropertyType.LAND,
			hasBuilding: propertyType === RVPropertyType.UNIT,
		};
		return update;
	}

	loadAreas = () => {
		let p = "";
		showLoading();
		this.setState( {
			isNoProperty: false,
		} );
		if ( this.props.env.participantId ) {
			p = this.props.env.participantId + "";
		}
		this.apiHandler = ( resp: any ) => {
			hideLoading();
			this.setState( {
				areas: resp ? sortRVXLookups( resp ) : [],
			} );
		};
		this.apiExHandler = ( reason: any ) => {
			hideLoading();
			if ( reason && reason.type && reason.type === "ValidationError" ) {
				showMessage( reason.message );
			} else {
				showMessage( reason.toString() );
			}
		};
		RVApi.loadLookupsX( "area", p )
		.then( RVApi.validateResponseX )
		.then( this.apiHandler )
		.catch( this.apiExHandler );
	};

	loadLands = ( areaId: number ) => {
		if ( !this.state.hasLand ) return;
		if ( ( areaId + "" ) === ( this.state.area + "" ) ) {
			return;
		}
		showLoading();
		this.setState( {
			land: undefined,
			building: undefined,
			isNoProperty: false,
			lands: [],
		} );
		this.apiHandler = ( resp: PropertiesResponse ) => {
			hideLoading();
			if ( resp && resp.PropertiesList && resp.PropertiesList.length > 0 ) {
				this.setState( {
					lands: resp.PropertiesList,
				}, () => {
					if ( resp.PropertiesList && resp.PropertiesList.length === 1 ) {
						let property = resp.PropertiesList[ 0 ];
						this.setState( {
							land: property,
						}, () => {
							this.loadBuildings( property, true );
						} );
					}
				} );
			} else {
				this.setState( {
					lands: [],
					isNoProperty: true,
				} );
			}
		};
		this.apiExHandler = ( reason: any ) => {
			hideLoading();
			if ( reason && reason.type && reason.type === "ValidationError" ) {
				if ( reason.codes && reason.codes.length === 1 && reason.codes[ 0 ].Identity === 2 ) {
					this.setState( {
						isNoProperty: true,
					} );
				} else
					showMessage( reason.message );
			} else {
				showMessage( reason.toString() );
			}
		};
		const env = this.props.env;
		const opts = { ...DefaultSearchOption };
		populateSearchOptions( opts, env );
		opts.searchTypeId = RVSearchType.Participant;
		opts.searchedPropertyTypeId = RVPropertyType.LAND;
		opts.areaId = areaId;
		RVApi.searchProperties( opts )
		.then( RVApi.validateResponse )
		.then( this.apiHandler )
		.catch( this.apiExHandler );
	};
	loadBuildings = ( land: Property, force: boolean = false ) => {
		if ( !this.state.hasBuilding ) return;
		let lid = LandPickerItemFormatter( land );
		if ( !force && ( lid + "" ) === ( this.state.land + "" ) ) {
			return;
		}
		showLoading();
		this.setState( {
			building: undefined,
			buildings: [],
			isNoProperty: false,
		} );
		this.apiHandler = ( resp: PropertiesResponse ) => {
			hideLoading();
			if ( resp && resp.PropertiesList && resp.PropertiesList.length > 0 ) {
				this.setState( {
					buildings: resp.PropertiesList,
				}, () => {
					if ( resp.PropertiesList && resp.PropertiesList.length === 1 ) {
						this.setState( {
							building: resp.PropertiesList[ 0 ], // BuildingPickerItemSelector.toSelector(resp.PropertiesList[0]),
						} );
					}
				} );
			} else {
				this.setState( {
					buildings: [],
					isNoProperty: true,
				} );
			}
		};
		this.apiExHandler = ( reason: any ) => {
			hideLoading();
			if ( reason && reason.type && reason.type === "ValidationError" ) {
				if ( reason.codes && reason.codes.length === 1 && reason.codes[ 0 ].Identity === 2 ) {
					this.setState( {
						isNoProperty: true,
					} );
				} else
					showMessage( reason.message );
			} else {
				showMessage( reason.toString() );
			}
		};
		const env = this.props.env;
		const opts = { ...DefaultSearchOption };
		populateSearchOptions( opts, env );
		opts.searchTypeId = RVSearchType.Participant;
		opts.searchedPropertyTypeId = RVPropertyType.BUILDING;
		opts.areaId = this.state.area?.Identity;
		opts.landNo = land.LandNo;
		opts.landSubNo = land.LandSubNo;
		RVApi.searchProperties( opts )
		.then( RVApi.validateResponse )
		.then( this.apiHandler )
		.catch( this.apiExHandler );
	};

	componentWillUnmount() {
		this.apiHandler = NullHandler;
		this.apiExHandler = NullHandler;
	}

	componentDidMount() {
		this.loadAreas();
	}

	updateParams( opts: SearchOption, env: RVEnvironment ): void {
		const { hasBuilding, hasLand, area, land, building, lands, buildings } = this.state;
		opts.areaId = area?.Identity;
		if ( hasLand ) {
			opts.landNo = land?.LandNo;
			opts.landSubNo = land?.LandSubNo;
		}
		if ( hasBuilding ) {
			opts.buildingName = LocaleUtil.getLocalValue( {
				en: building?.BuildingNameEn,
				ar: building?.BuildingNameAr,
			}, "en" );
		}
	}

	validate( opts: SearchOption, env: RVEnvironment ): boolean {
		const { hasBuilding, hasLand } = this.state;
		let isValid: boolean = true;
		let errorContainer: ErrorContainer = {};
		isValid = checkNonEmpty( this.state, "area", errorContainer ) && isValid;
		if ( hasLand ) {
			isValid = checkNonEmpty( this.state, "land", errorContainer ) && isValid;
		}
		if ( hasBuilding ) {
			isValid = checkNonEmpty( this.state, "building", errorContainer ) && isValid;
		}
		this.setState( {
			errorContainer: errorContainer,
		} );
		return isValid;
	}

	render() {
		const kvProp = { inputClassName: "form-control", holder: this, containerClassName: "col-md-6 form-group" };
		const { areas, hasBuilding, hasLand, lands, area, isNoProperty, errorContainer, buildings } = this.state;
		return (
			<React.Fragment>
				<div className="row">
					<div className="col-md-4">
						<PickerInput
							{ ...kvProp }
							label={ Strings.area }
							id={ "area" }
							items={ areas }
							formatter={ item => LocaleUtil.getLocalValue( item ) }
							toIdentifier={ ( item: RVPicketItem ) => item.Identity + "" }
							containerClassName="form-group field-style select-control"
							required={ true }
							error={ errorContainer.area }
							onValueChange={ ( value, extra ) => {
								this.loadLands( value!.Identity );
							} }
						/>
					</div>
					{ hasLand && area && !isNoProperty &&
						<div className="col-md-4">
							<PickerInput
								{ ...kvProp }
								label={ Strings.land_number }
								id={ "land" }
								items={ lands }
								formatter={ LandPickerItemFormatter }
								toIdentifier={ ( item: Property ) => {
									let landNumber = item.LandNo;
									if ( item.LandSubNo && item.LandSubNo !== "0" ) {
										landNumber = landNumber + "-" + item.LandSubNo;
									}
									return landNumber;
								} }
								containerClassName="form-group field-style select-control"
								required={ true }
								error={ errorContainer.land }
								onValueChange={ ( value, extra ) => {
									this.loadBuildings( value! );
								} }
								disabled={ isNoProperty }
							/>
						</div>
					}
					{
						hasBuilding && lands && !isNoProperty &&
						<div className="col-md-4">
							<PickerInput
								{ ...kvProp }
								label={ Strings.building }
								id={ "building" }
								items={ buildings }
								formatter={ BuildingPickerItemFormatter }
								toIdentifier={ ( item: Property ) => {
									return item.BuildingPropertyId + "";
								} }
								containerClassName="form-group field-style select-control"
								required={ true }
								error={ errorContainer.building }
								disabled={ isNoProperty }
							/>
						</div>
					}
				</div>
				{ isNoProperty === true &&
					<div className="row">
						<div className="col-xl-12 col-lg-12 col-md-12 col-sm-12 col-12">
							<div className="alert alert-warning">{ Strings.no_data_found }</div>
						</div>
					</div>
				}
			</React.Fragment>
		);
	}

}
